import { Title } from '@solidjs/meta';
import { ErrorBoundary, For, Show, createMemo, createSignal } from 'solid-js';
import { createAsync, Navigate, useLocation } from '@solidjs/router';
import {
	Button,
	Container,
	Errors,
	Form,
	Heading,
	Input,
	Link,
	Page,
	Section,
	Grid,
	GridFive,
	GridSeven,
	Picture,
	Hero,
	Field,
} from '@troon/ui';
import { IconExternalLink } from '@troon/icons/external-link';
import { useTrackEvent } from '@troon/analytics';
import { IconCopy } from '@troon/icons/copy';
import { RichText } from '@troon/contentful/rich-text';
import { useUser } from '../../../../providers/user';
import NotFound from '../../_/404';
import { cachedQuery } from '../../../../graphql/cached-get';
import { Benefit, gql, mutationAction, TroonCardSubscriptionProductType, useMutation } from '../../../../graphql';
import { createContentfulRequest } from '../../../(api)/content/[model]/_client';
import type { RouteDefinition, RouteSectionProps } from '@solidjs/router';

export default function GolfPassBenefit(props: RouteSectionProps) {
	const user = useUser();
	const loc = useLocation();
	const [copied, setCopied] = createSignal(false);
	const track = useTrackEvent();
	const page = createAsync(
		async () => {
			if (props.params.benefit! in slugToBenefit) {
				return getPage(props.params.benefit!);
			}
			return null;
		},
		{ deferStream: true },
	);

	const details = createMemo(() => slugToBenefit[props.params.benefit! as keyof typeof slugToBenefit]);

	const claimedCode = createAsync(
		async () => {
			const benefit = details()?.benefit;
			if (benefit) {
				return getClaimedCode({ benefit });
			}
			return null;
		},
		{
			deferStream: true,
		},
	);
	const claimAction = useMutation(claimCodeMutation);

	return (
		<ErrorBoundary fallback={<NotFound />}>
			<Show when={user()} fallback={<Navigate href={`/auth?redirect=${loc.pathname}`} />}>
				<Show
					when={
						details()?.product === TroonCardSubscriptionProductType.TroonAccess
							? user()?.troonAccessProductType
							: user()?.troonAccessProductType === details()?.product
					}
					fallback={<NotFound />}
				>
					<Show when={page()?.fields}>
						{(fields) => (
							<>
								<Title>{fields().title} | Benefits | Membership | Golf with Access</Title>
								<Hero
									src={fields().heroImage?.fields.file?.url}
									breadcrumbs={[
										{ href: '/membership', title: 'Membership' },
										{ href: '/membership/benefits', title: 'Benefits' },
										{ href: `/membership/benefits/${props.params.benefit}`, title: fields().title },
									]}
								>
									<Heading as="h1">
										<Show when={fields().logo} fallback={fields().title}>
											{(logo) => (
												<>
													<Picture
														src={logo().fields.file?.url}
														alt={fields().title}
														sizes="(min-width: 1024px) 35vw, (min-width: 768px) 50vw, 95vw"
														width={Math.min(logo().fields.file?.details.image?.width ?? 500, 500)}
														height={Math.round(
															Math.min(logo().fields.file?.details.image?.width ?? 500, 500) *
																((logo().fields.file?.details.image?.height ?? 300) /
																	(logo().fields.file?.details.image?.width ?? 300)),
														)}
														mode="contain"
														class="rounded-lg"
														preload
													/>
													<span class="sr-only">{fields().title}</span>
												</>
											)}
										</Show>
									</Heading>
									<p class="text-lg">{fields().summary}</p>
								</Hero>

								<Container>
									<Page>
										<Grid>
											<GridSeven>
												<Section>
													<RichText document={fields().content!} />
												</Section>
											</GridSeven>

											<GridFive>
												<Section class="sticky top-24 gap-4 rounded-lg border border-neutral p-4 md:p-6">
													<Heading as="h2" size="h4">
														How to Redeem
													</Heading>

													<Show
														when={claimedCode()?.codes.length && claimedCode()?.codes}
														fallback={
															<Form action={claimAction} document={claimQuery}>
																<input type="hidden" name="benefit" value={details()?.benefit} />
																<p>Request your exclusive promo code for {fields().title}</p>
																<Errors />
																<Button type="submit">Claim your {fields().title} code</Button>
															</Form>
														}
													>
														{(codes) => (
															<>
																<p>
																	Copy your exclusive promo code and follow the link to redeem your {fields().title}{' '}
																	benefit.
																</p>

																<For each={codes() as Array<{ code: string }>}>
																	{(code) => (
																		<div class="flex gap-4">
																			<Field name="code" class="grow">
																				<Field.Label class="sr-only">
																					Your unique {fields().title} redemption code
																				</Field.Label>
																				<Input readonly value={code.code} />
																				<Field.Description>
																					<Show when={copied()} fallback={<>&nbsp;</>}>
																						<p class="text-brand-600">Code has been copied to your clipboard!</p>
																					</Show>
																				</Field.Description>
																				<Field.Errors />
																			</Field>
																			<Button
																				onClick={() => {
																					window.navigator.clipboard.writeText(code.code);
																					setCopied(true);
																				}}
																				class="size-fit shrink grow-0"
																			>
																				<IconCopy />
																				<span class="sr-only">Copy {code.code}</span>
																			</Button>
																		</div>
																	)}
																</For>

																<Button
																	as={Link}
																	href={details()?.url ?? '/'}
																	appearance="secondary"
																	onClick={() => {
																		track('redeemBenefit', { benefit: props.params.benefit });
																	}}
																>
																	Redeem Your Code <IconExternalLink />
																</Button>
															</>
														)}
													</Show>
												</Section>
											</GridFive>
										</Grid>
									</Page>
								</Container>
							</>
						)}
					</Show>
				</Show>
			</Show>
		</ErrorBoundary>
	);
}

const slugToBenefit: Record<string, { benefit: Benefit; url: string; product: TroonCardSubscriptionProductType }> = {
	inkind: {
		benefit: Benefit.Inkind,
		url: 'https://app.inkind.com/offer/2XZEUFOJ',
		product: TroonCardSubscriptionProductType.TroonAccess,
	},
};

const getPage = createContentfulRequest('page');

const claimedQuery = gql(`query claimedBenefitCode($benefit: Benefit!) {
  codes: getClaimedBenefitCodes(benefit: $benefit) { code }
}`);
const getClaimedCode = cachedQuery(claimedQuery);

const claimQuery = gql(`mutation claimBenefitCode($benefit: Benefit!) {
  code: claimBenefitCode(benefit: $benefit) { code }
}`);
const claimCodeMutation = mutationAction(claimQuery, {
	revalidates: ['claimedBenefitCode'],
	track: {
		event: 'claimBenefit',
		transform: (data) => ({
			benefit: data.get('benefit') as string,
		}),
	},
});

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
